<template>
  <div class="modal-overlay" @click.stop>
    <div class="modal-content">
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('camstudy.imageModal.title') }}</h2>
      </div>
      <div class="modal-body">
        <div v-if="showCropper" class="preview-container">
          <cropper class="cropper"
                   :src="uploadImage.path"
                   :options="{ aspectRatio: 1 }"
                   :stencil-props="{ aspectRatio: 334 / 222 }"
                   @change="onChangeCroppedImage"
          />
        </div>
        <div v-else-if="inProgressUpload" class="preview-container loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ $t('camstudy.imageModal.processing') }}</p>
        </div>
        <div v-else class="preview-container">
          <img
              :src="selectedDefaultImage"
              alt="defaultImage"
              class="main-preview"
          />
        </div>
        <div class="thumbnail-list">
          <div
              v-for="(img, index) in defaultImageList"
              :key="index"
              class="thumbnail-item"
              :class="{ 'thumbnail-selected': selectedIndex === index }"
              @click="handleThumbnailClick(index)"
          >
            <img :src="img" :alt="$t('camstudy.imageModal.thumbnail')" />
          </div>
          <button class="thumbnail-item upload" @click="onOpenFile" :disabled="inProgressUpload"></button>
        </div>
      </div>
      <div class="modal-footer" @click.stop>
        <button class="cancel-btn" @click="handleCancel">{{ $t('common.cancel') }}</button>
        <button class="confirm-btn" :disabled="inProgressUpload" @click="handleConfirm">{{ $t('common.confirm') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import {cdnImgUrl} from '@/utils/ImageUrl'
import {mapGetters} from 'vuex'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css';
export default {
  name: 'RoomImageSettingModal',
  components: {
    Cropper
  },
  data() {
    return {
      selectedIndex: null,
      selectedDefaultImage: null,
      defaultImageList: [],
      allowedFileExtensions: ['jpg', 'jpeg', 'png', 'gif'],
      inProgressUpload: false,
      showCropper: false,
      uploadImage: {
        file: null,
        path: null
      },
      croppedImage: {
        coordinates: {},
        path: null,
        file: null
      }
    };
  },
  created() {
    for (let i = 4; i < 7; i++) {
      const defaultImg = cdnImgUrl(`room/common/img/title/roomImg_00${i}.jpg`);
      this.defaultImageList.push(defaultImg);
    }

    if (this.getRoomImagePath) {
      this.selectedDefaultImage = cdnImgUrl(this.getRoomImagePath)
      if (this.getRoomImagePath.startsWith('room/common/img/title/roomImg_00')) {
        const imageNum = this.getRoomImagePath.split('_')[1].split('.')[0]
        Number.isNaN(parseInt(imageNum))
            ? this.selectedIndex = null
            : this.selectedIndex = parseInt(imageNum) - 4
      } else {
        this.selectedIndex = null
      }
    } else {
      const imageIdx = Math.floor(Math.random() * 3)
      this.selectedDefaultImage = this.defaultImageList[imageIdx]
      this.selectedIndex = imageIdx
    }
  },
  destroyed() {
    this.resetCropImage()
    this.resetUploadImage()
  },
  computed: {
    ...mapGetters('camstudy', [
      'getRoomImagePath'
    ]),
    ...mapGetters('user', [
      'hasEmceeRole'
    ]),
  },
  methods: {
    handleThumbnailClick(index) {
      this.selectedIndex = index;
      this.selectedDefaultImage = this.defaultImageList[index];
      this.showCropper = false
      this.resetCropImage()
      this.resetUploadImage()
    },
    async handleConfirm() {
      if (this.showCropper) {
        if (!this.croppedImage.file) {
          this.$camsAlert(this.$t('camstudy.imageModal.noSelectedImage'))
          return
        }
        this.$emit('confirm', { ...this.croppedImage, defaultImage: false });
      } else {
        if (this.selectedIndex === null) {
          this.$camsAlert(this.$t('camstudy.imageModal.noSelectedImage'))
          return
        }
        this.$emit('confirm', {
          path: `room/common/img/title/roomImg_00${this.selectedIndex + 4}.jpg`,
          defaultImage: true
        });
      }
    },
    handleCancel() {
      this.$emit('close');
    },
    validExtension(file) {
      const fileNameArr = file.name.split('.')
      const extension = fileNameArr[fileNameArr.length - 1].toLowerCase()
      return this.allowedFileExtensions.includes(extension)
    },
    resetUploadImage() {
      if (this.uploadImage.path) {
        URL.revokeObjectURL(this.uploadImage.path)
      }
      this.uploadImage = {
        file: null,
        path: null
      }
    },
    resetCropImage() {
      if (this.croppedImage.path) {
        URL.revokeObjectURL(this.croppedImage.path)
      }
      this.croppedImage = {
        coordinates: {},
        path: null,
        file: null
      }
    },
    async onOpenFile() {
      if (!this.hasEmceeRole()) {
        this.$camsAlert(this.$t('camstudy.imageModal.noRole'))
        return
      }
      if (this.inProgressUpload) {
        return
      }

      try {
        this.inProgressUpload = true
        const files = await this.getFileChooser({
          multiple: false,
          accept: this.allowedFileExtensions.join(","),
          size: 1
        })

        if (files && files.length > 0) {
          const file = files[0]
          if (!this.validExtension(file)) {
            this.$camsAlert(this.$t('camstudy.imageModal.fileTypeError'))
            return
          }
          // max 100kb
          const compressedImage = await this.compressImage(file, 100 * 1024)
          this.resetUploadImage()
          this.uploadImage.file = compressedImage
          this.uploadImage.path = URL.createObjectURL(compressedImage)
          this.showCropper = true
          this.selectedIndex = null
        }
      } catch (e) {
        console.warn('## onOpenFile warn -> reason[%s]', e)
      } finally {
        this.inProgressUpload = false
      }
    },
    onChangeCroppedImage({coordinates, canvas}) {
      this.croppedImage.coordinates = coordinates
      this.croppedImage.path = canvas.toDataURL()
      canvas.toBlob(blob => {
        this.croppedImage.file = new File([blob], this.uploadImage.file?.name, {
          type: this.uploadImage.file?.type,
          lastModified: Date.now()
        })
      })
    },
  }
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.50);
  z-index: 100;
}
.modal-content {
  position: absolute;
  padding-top: 6px;
  background: #313131;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}
[grm-device-mobile]{
  .modal-content {
    @media(orientation: portrait) {
      left: 8px;
      right: 8px;
      bottom: 72px;
    }
    @media(orientation: landscape) {
      left: 50%;
      width: 360px;
      bottom: 106px;
      transform: translateX(-50%);
      top: 23px;
    }
    .modal-body{
      @media(orientation: landscape) {
        height: calc(100dvh - 57px - 195px);
        overflow-y: scroll;
      }
    }
  }
}
html:not([grm-device-mobile]) {
  .modal-content {
    left: 50%;
    top: 50%;
    width: 360px;
    transform: translate(-50%, -50%);
  }
}
.modal-header {
  padding: 10px 20px;
}
.modal-title {
  color: #FFF;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}
.modal-body {
  &::-webkit-scrollbar {
    display: none;
  }
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.preview-container {
  width: 100%;
  aspect-ratio: 334 / 222;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1F1F1F;
  border-radius: 6px;
}
.main-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}
.thumbnail-list {
  display: flex;
  gap: 10px;
  padding: 24px 0 12px;
  width: 100%;
}
.thumbnail-item {
  width: 100%;
  aspect-ratio:  1 / 1;
  cursor: pointer;
  border-radius: 6px;

  &.upload{
    border-radius: 6px;
    background: #1F1F1F url("~@/assets/images/camstudy/ico_upload_white.svg") no-repeat center;
  }
}
.thumbnail-item img {
  width: 100%;
  object-fit: cover;
  border-radius: 6px;
  aspect-ratio: 1 / 1;
}
.thumbnail-selected {
  position: relative;
  &::after{
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(1, 111, 242, 0.50) url("~@/assets/images/camstudy/ico_check_white.svg") no-repeat center;
    background-size: auto 24px;
    border: 4px solid #007bff;
    border-radius: 6px;
  }
}
.modal-footer {
  border-top: 0.333px solid rgba(255, 255, 255, 0.20);
  display: flex;
}
.cancel-btn, .confirm-btn {
  padding: 15px 0 14px 0;
  border: none;
  outline: none;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  line-height: 150%;
  width: 100%;
}
.cancel-btn {
  color: #999;
}
.confirm-btn {
  color: #FFF;
  font-weight: 700;
}
.confirm-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  aspect-ratio: 334 / 222;
}
.loading-spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #007bff;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}
.loading-text {
  color: #fff;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.cropper {
  width: 100%;
  aspect-ratio: 334 / 222;
}
</style>
